import { Injectable, OnDestroy, Renderer2 } from '@angular/core';
import { BehaviorSubject, catchError, concatMap, defer, filter, first, firstValueFrom, from, lastValueFrom, map, merge, Observable, of, range, Subject, switchMap, take, takeUntil, takeWhile, tap, timeout, timer } from 'rxjs';
import { environment } from '../../../environments/environment.dev';
import { LoadingService } from './loading.service';

export enum CardReaderCommandCode {
  ReadCardSingle = 'ReadCardSingle',
  ReadCardContinuous = 'ReadCardContinuous',
  CardData = 'CardData',
  CardError = 'CardError',
  ReaderEvent = 'ReaderEvent',
  ReportStatus = 'ReportStatus',
  CancelRead = 'CancelRead',
  ListCardReaders = 'ListCardReaders',
  SelectReader = 'SelectReader',
  Info = 'Info',
  RetryReader = 'RetryReader',
}

export enum CardReaderStatusCode {
  Unknown = 0,
  Ready = 1,
  NotReady = 2,
  Busy = 3,
  SpecifyReader = 4,
  NoDevices = 5,
  ReaderDisconnect = 6,
}

export enum CardReaderStatus {
  Unknown = 'Unknown',
  Ready = 'Ready',
  NotReady = 'Not Ready',
  Busy = 'Busy',
  SpecifyReader = 'Specify Reader',
  NoDevices = 'No devices available',
  ReaderDisconnect = 'Reader disconnected',
}

export interface CardReaderResponse<T> {
  code: CardReaderCommandCode;
  port: string;
  status: CardReaderStatus;
  statusCode: number;
  success: boolean;
  result?: T;
}

export interface CardReaderDeviceDescriptor {
  deviceId: string;
  deviceName: string;
}

export enum CardReaderServiceStatus {
  OS_NOT_SUPPORTED = 'OS_NOT_SUPPORTED',
  CARD_READER_CONNECTED = 'CARD_READER_CONNECTED',
  CARD_READER_SOCKET_ERROR = 'CARD_READER_SOCKET_ERROR',
  CARD_READER_DEVICE_DISCONNECTED = 'CARD_READER_DEVICE_DISCONNECTED',
}

export interface CommandResult<T> {
  value: T;
  success: boolean;
  status: CardReaderStatus;
  statusCode: number;
}

export interface InfoCommandResult extends CommandResult<CardReaderConnectorInfo> {}

export interface CardReaderConnectorInfo {
  version: string;
  commandSet: number;
}

export interface CardReaderConnectorLaunchStatus {
  status: 'launching' | 'cancelled' | 'launched' | 'failed';
  currentAttempt: number;
  maxAttempts: number;
}

/**
 * Service providing connectivity and command execution capabilities to card reader connector native app.
 */
@Injectable({
  providedIn: 'root'
})
export class CardReaderService implements OnDestroy {
  public static readonly ConnectorProtocolUrl = environment?.production ? 'tuke-portal-cardreader://-' : 'tuke-portal-cardreader-dev://-';

  readonly CARD_STATUS_RESET_TIMEOUT = 5000;
  readonly CARD_READER_PORT = 46780;
  readonly CARDREADERCONNECTOR_CURRENT_VERSION = '1.1.2.0';

  private socket: WebSocket | null = null;
  private readType: 'single' | 'continuous' | null = null; // whether we are currently reading a single card or multiple cards continuously
  private mConnectorVersion: string;
  private mIsLatestVersion: boolean;

  private readonly currentCardStatus$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private readonly currentCardNumber$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private readonly cardReaderServiceStatus$: BehaviorSubject<CardReaderServiceStatus | null> = new BehaviorSubject<CardReaderServiceStatus | null>(null);
  private readonly launchStatus$: BehaviorSubject<CardReaderConnectorLaunchStatus | null> = new BehaviorSubject<CardReaderConnectorLaunchStatus | null>(null);
  private readonly socketConnected$: BehaviorSubject<{ success: boolean, error?: any } | null> = new BehaviorSubject<{ success: boolean, error?: any } | null>(null);
  private readonly cancelCommand$: Subject<void> = new Subject<void>();
  private readonly message$: Subject<CardReaderResponse<any>> = new Subject<CardReaderResponse<any>>();
  private readonly read$: Subject<string> = new Subject<string>();
  private readonly readError$: Subject<void> = new Subject<void>();
  private readonly destroy$: Subject<void> = new Subject<void>();

  /**
   * Constant for error command result
   */
  private readonly ErrorCommandResult: CommandResult<any> = {
    success: false,
    statusCode: CardReaderStatusCode.Unknown,
    status: CardReaderStatus.Unknown,
    value: undefined,
  } as const;

  constructor() {
    // check if we are supported and if yes, setup global message processing
    if (this.isOsSupported()) {
      this.setupMessageHandler();
    }
  }

  /**
   * Gets boolean indicating if websocket is currently connected.
   */
  get isConnected(): boolean {
    return this.socket != null && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Gets boolean indicating if websocket is currently connecting.
   */
  get isConnecting(): boolean {
    return this.socket != null && this.socket.readyState === WebSocket.CONNECTING;
  }

  /**
   * Gets version of connected connector.
   */
  get connectorVersion(): string {
    return this.mConnectorVersion;
  }

  /**
   * Gets required connector version.
   */
  get connectorCurrentVersion(): string {
    return this.CARDREADERCONNECTOR_CURRENT_VERSION;
  }

  /**
   * Gets boolean indicating matching versions of connector.
   */
  get isLatestVersion(): boolean {
    return this.mIsLatestVersion;
  }

  /**
   * Gets current card status.
   */
  get cardStatus(): boolean | null {
    return this.currentCardStatus$.value;
  }

  /**
   * Gets current service status.
   */
  get serviceStatus(): CardReaderServiceStatus {
    return this.cardReaderServiceStatus$.value;
  }

  /**
   * Gets current card serial number.
   */
  get currentCardNumber(): string {
    return this.currentCardNumber$.value;
  }

  ngOnDestroy(): void {
    this.disconnect();

    this.socketConnected$.complete();
    this.cancelCommand$.complete();
    this.read$.complete();
    this.readError$.complete();
    this.message$.complete();
    this.launchStatus$.complete();

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Checks if the operating system is supported.
   * @returns True if supported, otherwise false.
   */
  isOsSupported(): boolean {
    const supported = /Windows/.test(navigator.userAgent);

    if (!supported) {
        this.cardReaderServiceStatus$.next(CardReaderServiceStatus.OS_NOT_SUPPORTED);
    }

    return supported;
  }

  /**
   * Attempts to start the native application.
   * @param containerElement Container element for hidden iframe.
   * @param renderer Instance of Angular Renderer2.
   * @param maxLaunchAttempts How many connection attempts to perform.
   * @returns Promise<boolean> returning true on success, otherwise false.
   */
  async launchApplication(containerElement: HTMLElement, renderer: Renderer2, maxLaunchAttempts: number = 10): Promise<boolean> {
    if (containerElement == null || renderer == null) {
      return false;
    }

    console.log('Attempting to start application...');
    this.launchStatus$.next(null);

    const existingIframe: HTMLIFrameElement = containerElement.querySelector('iframe');

    if (existingIframe) {
      renderer.removeChild(containerElement, existingIframe);
    }

    const iframe: HTMLIFrameElement = renderer.createElement('iframe');

    renderer.setAttribute(iframe, 'src', CardReaderService.ConnectorProtocolUrl);
    renderer.setStyle(iframe, 'display', 'none');

    renderer.appendChild(containerElement, iframe);

    let currentLaunchAttempt = 1;

    const startResult: boolean = await lastValueFrom(
      range(1, maxLaunchAttempts).pipe(
        concatMap(
          i => {
            currentLaunchAttempt = i;
            this.launchStatus$.next({
              status: 'launching',
              currentAttempt: currentLaunchAttempt,
              maxAttempts: maxLaunchAttempts,
            });

            console.log(`Retrying connection... ${currentLaunchAttempt}/${maxLaunchAttempts}`);

            return timer(5000).pipe(
              switchMap(() => from(this.testConnection(true)))
            );
          }
        ),
        takeUntil(merge(this.cancelCommand$, this.destroy$).pipe(
          tap({
            next: () => {
              this.launchStatus$.next({
                status: 'cancelled',
                currentAttempt: currentLaunchAttempt,
                maxAttempts: maxLaunchAttempts,
              });
            }
          })
        )),
        takeWhile(result => result === false, true),
      )
    );

    renderer.removeChild(containerElement, iframe);

    if (startResult) {
      this.launchStatus$.next({
        status: 'launched',
        currentAttempt: currentLaunchAttempt,
        maxAttempts: maxLaunchAttempts,
      });
    } else {
      this.launchStatus$.next({
        status: 'failed',
        currentAttempt: currentLaunchAttempt,
        maxAttempts: maxLaunchAttempts,
      });
    }

    return startResult;
  }

  /**
   * Returns current service status.
   * @returns Promise<CardReaderServiceStatus> returning current service status.
   */
  async currentServiceStatus(): Promise<CardReaderServiceStatus> {
    return firstValueFrom(this.cardReaderServiceStatus$);
  }

  /**
   * Tests connection to card reader connector.
   * @param keepConnection True to keep connection open after a successful test.
   * @returns Promise<boolean> returning true when test is successful, otherwise false.
   */
  async testConnection(keepConnection: boolean): Promise<boolean> {
    if (!this.isOsSupported()) {
      console.error('OS not supported.');
      return false;
    }

    if (this.isConnected) {
      return true;
    } else if (this.isConnecting) {
      const result = await this.waitToConnect();

      return result;
    }
    
    try {
      await this.connect();

      if (!keepConnection) {
        this.disconnect();
      }

      return true;
    } catch (e) {
      this.cardReaderServiceStatus$.next(CardReaderServiceStatus.CARD_READER_SOCKET_ERROR);
      console.error('Error connecting to card reader service', e);

      return false;
    }
  }

  /**
   * Connects to card reader connector.
   * @returns Promise<void>
   */
  async connect(): Promise<void> {
    LoadingService.show();

    if (this.socket) {
      console.log('Already connected.');

      LoadingService.hide();
      return;
    }

    this.socketConnected$.next(null);

    this.socket = new WebSocket('wss://localhost:' + this.CARD_READER_PORT + '/api/websocket', ['tuke-portal-cardreader-v1']);

    this.socket.onopen = async () => {
      const statusResponse: CommandResult<any> = await this.reportStatus();

      if (statusResponse != null && (statusResponse.statusCode === CardReaderStatusCode.SpecifyReader || statusResponse.statusCode === CardReaderStatusCode.NoDevices)) {
        this.cardReaderServiceStatus$.next(CardReaderServiceStatus.CARD_READER_DEVICE_DISCONNECTED);
      } else {
        this.cardReaderServiceStatus$.next(CardReaderServiceStatus.CARD_READER_CONNECTED);
      }
      
      try {
        const infoResponse: InfoCommandResult = await this.info();

        if (infoResponse != null) {
          this.updateIsLatestVersion(infoResponse.value.version);
        } else {
          this.updateIsLatestVersion(undefined);
        }
      } catch (e) {
        console.error(e);
        this.updateIsLatestVersion(undefined);
      }

      this.socketConnected$.next({ success: true });

      console.log('WebSocket connected.');
      LoadingService.hide();

      return;
    };

    this.socket.onmessage = (event) => {
      if (event && event.data) {
        const commandResult: CardReaderResponse<any> = JSON.parse(event.data);

        this.message$.next(commandResult);
      }
    };

    this.socket.onerror = ((service: CardReaderService, reject: (reason: any) => void) => {
      return function(event: Event) {
        LoadingService.hide();
        console.error(`WebSocket error: ${event}`);
        
        if (this.readyState === WebSocket.CLOSING || this.readyState === WebSocket.CLOSED) {
          service.cardReaderServiceStatus$.next(CardReaderServiceStatus.CARD_READER_SOCKET_ERROR);
          service.socket = null;
        }
        
        reject(event);
      }
    })(
      this,
      (event) => {
        this.socketConnected$.next({ success: false, error: event });
        this.socketConnected$.next(null);
      }
    );

    this.socket.onclose = () => {
      this.readType = null;
      this.socket = null;

      this.socketConnected$.next({ success: false });
      this.socketConnected$.next(null);

      LoadingService.hide();
      console.log('WebSocket disconnected.');
    };

    const connectResult: boolean = await this.waitToConnect(5000); // time out after 5 seconds

    // if connection attempt is unsuccessful or timed out, we need to clean up the websocket object and reset.
    if (!connectResult) {
      this.disconnect();

      throw new Error('Websocket failed to connect.');
    }
  }

  /**
   * Disconnects from card reader connector.
   */
  disconnect(): void {
    this.readType = null;
    this.updateIsLatestVersion(undefined);
    
    if (this.launchStatus$.value != null && this.launchStatus$.value.status !== 'launching') {
      this.cancelCommand$.next();
    }

    if (this.socket != null) {
      try {
        this.socket.close();
      } catch (error) {
        console.error(error);
      } finally {
        this.socket = null;
      }
    } else {
      console.log('Not connected.');
    }
  }

  /**
   * Reads a single card.
   * @returns Promise<CommandResult> returning command result containing card serial number if successful.
   */
  async readSingle(): Promise<CommandResult<any>> {
    const currentStatus: CardReaderServiceStatus = await this.currentServiceStatus();

    if (currentStatus !== CardReaderServiceStatus.CARD_READER_CONNECTED) {
      return this.ErrorCommandResult;
    }

    this.readType = 'single';

    try {
      const result: CardReaderResponse<any> = await this.executeCommand(CardReaderCommandCode.ReadCardSingle, true);

      if (result != null && result.success) {
        const value: string = await firstValueFrom(
          merge(
            this.read$.pipe(takeUntil(this.cancelCommand$)),
            this.cancelCommand$.pipe(take(1), map(() => 'cancelled'))
          ).pipe(
            takeUntil(this.destroy$),
            first()
          ));

        this.readType = null;

        if (value === 'cancelled') {
          return null;
        }

        return {
          success: true,
          status: CardReaderStatus.Unknown,
          statusCode: CardReaderStatusCode.Unknown,
          value,
        };
      }

      this.readType = null;

      return this.ErrorCommandResult;
    } catch (error) {
      this.readType = null;
      console.error(error);
      return this.ErrorCommandResult;
    }
  }

  /**
   * Begins reading multiple cards. This command must be cancelled when done - only one connection can read cards at the same time.
   * @returns Promise<CommandResult> returning command result.
   */
  async beginRead(): Promise<CommandResult<any>> {
    const currentStatus: CardReaderServiceStatus = await this.currentServiceStatus();

    if (currentStatus !== CardReaderServiceStatus.CARD_READER_CONNECTED) {
      return this.ErrorCommandResult;
    }

    this.readType = 'continuous';

    try {
      const result: CardReaderResponse<any> = await this.executeCommand(CardReaderCommandCode.ReadCardContinuous, true);
  
      return this.mapCommandResult(result);
    } catch (error) {
      console.error(error);
      return this.ErrorCommandResult;
    }
  }

  /**
   * Cancels current read operation.
   * @returns Promise<CommandResult> returning command result.
   */
  async cancelRead(): Promise<CommandResult<any>> {
    if (this.readType == null) {
      return {
        success: true,
        status: CardReaderStatus.Unknown,
        statusCode: CardReaderStatusCode.Unknown,
        value: undefined,
      };
    }

    try {
      const result: CardReaderResponse<any> = await this.executeCommand(CardReaderCommandCode.CancelRead, true);

      if (result.success) {
        this.readType = null;
      }

      return this.mapCommandResult(result);
    } catch (error) {
      console.error(error);
      return this.ErrorCommandResult;
    }
  }

  /**
   * Queries card reader connector for a list of available readers.
   * @returns Promise<CommandResult> returning command result containing list of card readers if successful.
   */
  async listCardReaders(): Promise<CommandResult<CardReaderDeviceDescriptor[]>> {
    try {
      const result: CardReaderResponse<CardReaderDeviceDescriptor[]> = await this.executeCommand(CardReaderCommandCode.ListCardReaders, true);

      return this.mapCommandResult(result);
    } catch (error) {
      console.error(error);
      return this.ErrorCommandResult;
    }
  }

  /**
   * Queries card reader connector for current status.
   * @returns Promise<CommandResult> returning command result.
   */
  async reportStatus(): Promise<CommandResult<any>> {
    try {
      const result: CardReaderResponse<any> = await this.executeCommand(CardReaderCommandCode.ReportStatus, true);
  
      return this.mapCommandResult(result);
    } catch (error) {
      console.error(error);
      return this.ErrorCommandResult;
    }
  }

  /**
   * Tells card reader connector to try to automatically reconnect the card reader.
   * @returns Promise<CommandResult> returning command result.
   */
  async retryReader(): Promise<CommandResult<any>> {
    try {
      const result: CardReaderResponse<any> = await this.executeCommand(CardReaderCommandCode.RetryReader, true);
  
      if (result.success) {
        this.cardReaderServiceStatus$.next(CardReaderServiceStatus.CARD_READER_CONNECTED);
      }

      return this.mapCommandResult(result);
    } catch (error) {
      console.error(error);
      return this.ErrorCommandResult;
    }
  }

  /**
   * Tells card reader connector to connect to card reader specified by a device ID.
   * @param deviceId The device ID of card reader to connect to.
   * @returns Promise<CommandResult> returning command result.
   */
  async selectCardReader(deviceId: string): Promise<CommandResult<any>> {
    if (deviceId == null || deviceId === '') {
      console.log('No device ID provided.');
      return this.ErrorCommandResult;
    }

    try {
      console.log(`Selecting device: ${deviceId}`);
      const result: CardReaderResponse<any> = await this.executeCommand(CardReaderCommandCode.SelectReader, true);

      return this.mapCommandResult(result);
    } catch (error) {
      console.error(error);
      return this.ErrorCommandResult;
    }
  }

  /**
   * Queries card reader connector for information about itself.
   * @returns Promise<CommandResult> returning command result.
   */
  async info(): Promise<InfoCommandResult> {
    try {
      const result: CardReaderResponse<CardReaderConnectorInfo> = await this.executeCommand(CardReaderCommandCode.Info, true);

      return this.mapCommandResult(result);
    } catch (error) {
      console.error(error);
      return this.ErrorCommandResult;
    }
  }

  /**
   * Listens to responses from card reader connector.
   * @returns Observable<CardReaderResponse>
   */
  getMessages() {
    return this.message$.asObservable();
  }

  /**
   * Listens to serial numbers of read cards.
   * @param cancelProof True if command cancellation should not complete this observable.
   * @returns Observable<string> providing serial numbers of read cards.
   */
  listenCardRead(cancelProof?: boolean): Observable<string> {
    return this.read$.pipe(
      takeUntil(cancelProof ? this.destroy$ : merge(this.destroy$, this.cancelCommand$))
    );
  }

  /**
   * Listens to current launch status changes.
   * @returns Observable<CardReaderConnectorLaunchStatus> providing current launch status.
   */
  listenLaunchStatus(): Observable<CardReaderConnectorLaunchStatus> {
    return this.launchStatus$.pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Listens to card status changes.
   * @returns Observable<boolean | null> providing current card status. True / false for success / failure or null for no status.
   */
  listenCardStatus(): Observable<boolean | null> {
    return this.currentCardStatus$.pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Listens to service status changes.
   * @returns Observable<CardReaderServiceStatus> providing current service status.
   */
  listenServiceStatus(): Observable<CardReaderServiceStatus> {
    return this.cardReaderServiceStatus$.pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Listens to read card serial numbers.
   * @returns Observable<string | null> providing current card serial number.
   */
  listenCardNumber(): Observable<string | null> {
    return this.currentCardNumber$.pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Listens to card read errors.
   * @param cancelProof True if command cancellation should not complete this observable.
   * @returns Observable<void>
   */
  listenCardReadError(cancelProof?: boolean): Observable<void> {
    return this.readError$.pipe(
      takeUntil(cancelProof ? this.destroy$ : merge(this.destroy$, this.cancelCommand$))
    );
  }

  /**
   * Sets current card status.
   * @param status True if successfully read.
   * @param cardNumber Serial number of card.
   * @param timeout True if card status should reset after a timeout.
   */
  setCurrentCardStatus(status: boolean, cardNumber: string, timeout = true) {
    this.currentCardStatus$.next(status);
    this.currentCardNumber$.next(cardNumber);

    if (timeout) {
      setTimeout(() => {
        this.currentCardStatus$.next(null);
        this.currentCardNumber$.next(null);
      }, this.CARD_STATUS_RESET_TIMEOUT);
    }
  }

  /**
   * Cancels currently executing commands.
   * @returns Promise<boolean> returning true if successful.
   */
  async cancelPendingCommands(): Promise<boolean> {
    let result: boolean = true;

    this.cancelCommand$.next();

    if (this.readType !== null) {
      const cancelReadResult = await this.cancelRead();

      if (cancelReadResult == null || !cancelReadResult.success) {
        result = false;
      }
    }

    return result;
  }

  /**
   * Resets current card status.
   */
  resetCurrentCardStatus() {
    this.currentCardStatus$.next(null);
    this.currentCardNumber$.next(null);
  }

  /**
   * Sends message through websocket to card reader connector.
   * @param code Command code.
   * @param args Command arguments.
   */
  private sendMessage(code: string, args: any[] = []): void {
    if (this.socket) {
      this.socket.send(JSON.stringify({
        code,
        port: 1, // For WebSocket, this value is not used
        arguments: args
      }));
    } else {
      console.log('Not connected.');
    }
  }

  /**
   * Executes a command through card reader connector.
   * @param command Command code.
   * @param waitResponse True to wait for immediate command result.
   * @returns Promise<CardReaderResponse> returning response message from card reader connector.
   */
  private async executeCommand<T = string>(command: string, waitResponse: boolean): Promise<CardReaderResponse<T>> {
    try {
      if (this.socket == null) {
        throw new Error('Websocket not connected.');
      } else if (this.socket.readyState === WebSocket.CONNECTING) {
        const waitSuccess: boolean = await this.waitToConnect();

        if (!waitSuccess) {
          LoadingService.hide();
          throw new Error('Websocket connect timeout.');
        }
      }

      if (waitResponse) {
        return firstValueFrom(
          defer(() => {
            const messageObservable: Observable<CardReaderResponse<T>> = this.message$.pipe(
              takeUntil(this.destroy$),
              filter((value: CardReaderResponse<T>) => value != null && value.code === command),
              first(),
            );
            this.sendMessage(command);

            return messageObservable;
          })
        );
      } else {
        this.sendMessage(command);
        return undefined;
      }
    } catch (error) {
      this.cardReaderServiceStatus$.next(CardReaderServiceStatus.CARD_READER_SOCKET_ERROR);
      console.error(error);
      throw new Error(error);
    }
  }

  /**
   * Maps card reader connector response to command result.
   * @param response Card reader response to map.
   * @returns CommandResult
   */
  private mapCommandResult(response: CardReaderResponse<any>): CommandResult<any> {
    if (!response) {
      return undefined;
    }

    return {
      success: response.success,
      value: response.result,
      status: response.status,
      statusCode: response.statusCode,
    };
  }

  /**
   * Sets up global message handler for independent message processing.
   */
  private setupMessageHandler(): void {
    this.message$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (commandResult: CardReaderResponse<any>) => {
        if (commandResult == null) {
          return;
        }

        switch (commandResult.code) {
          case CardReaderCommandCode.ReaderEvent:
            if (commandResult.statusCode === CardReaderStatusCode.ReaderDisconnect) {
              this.cardReaderServiceStatus$.next(CardReaderServiceStatus.CARD_READER_DEVICE_DISCONNECTED);
            }
            break;

          case CardReaderCommandCode.CardData:
            this.handleCardReadResult(commandResult);
            break;

          case CardReaderCommandCode.CardError:
            this.handleCardReadResult(commandResult);
            break;
        }
      }
    });
  }

  /**
   * Handles result of read operation.
   * @param commandResult Response from card reader connector.
   * @returns void
   */
  private handleCardReadResult(commandResult: CardReaderResponse<string>): void {
    if (commandResult.code !== CardReaderCommandCode.CardData && commandResult.code !== CardReaderCommandCode.CardError) {
      return;
    } else if (!commandResult.success || commandResult.result === '-1') {
      this.readError$.next();
      this.setCurrentCardStatus(false, null, this.readType === 'continuous');
      return;
    }

    this.read$.next(commandResult.result);
    this.setCurrentCardStatus(true, commandResult.result, this.readType === 'continuous');
  }

  /**
   * Waits until websocket connects to card reader connector, timing out after a specified amount of time if unable to connect.
   * @param connectTimeout timeout in milliseconds
   * @returns Promise<boolean> returning true if successful, otherwise false.
   */
  private waitToConnect(connectTimeout: number = 10000): Promise<boolean> {
    if (this.isConnected) {
      return Promise.resolve(true);
    }

    return firstValueFrom(
      this.socketConnected$.pipe(
        takeUntil(this.destroy$),
        filter(connecting => connecting != null),
        first(),
        timeout({
          first: connectTimeout,
        }),
        map((value) => {
          if (value.error != null) {
            throw value.error;
          }

          return value.success;
        }),
        catchError(() => {
          return of(false);
        })));
  }

  /**
   * Updates boolean determining if card reader connector is latest version.
   * @returns void
   */
  private updateIsLatestVersion(version: string): void {
    this.mConnectorVersion = version;

    if (version == null || version === '') {
      this.mIsLatestVersion = undefined;
      return;
    }

    this.mIsLatestVersion = this.mConnectorVersion === this.CARDREADERCONNECTOR_CURRENT_VERSION;
  }
}
